import React, { useCallback, useEffect, useMemo, useState } from 'react';

import type { ApiClient } from '../api/client';
import type { ExecutorTerminalIdentity, WorkerNode, TerminalIdentityAccount } from '../types';
import { reportErrorLocal } from '../utils/errors';
import ErrorBanner from './ErrorBanner';

type Props = {
  apiClient: ApiClient;
  executor: WorkerNode;
  setError: (message: string | null) => void;
};

type TerminalIdentityAllowlistResponse = {
  accounts: TerminalIdentityAccount[];
};

function accountLabel(account: TerminalIdentityAccount) {
  return `${account.username} (UID ${account.uid}, ${account.shell})`;
}

function userLabel(identity: ExecutorTerminalIdentity) {
  return identity.user_name?.trim() || identity.user_email?.trim() || 'Unknown product user';
}

/**
 * Maps a known Mission Control user (by exact email) to an executor-host account.
 * The product never sends a local account when it starts a terminal; the
 * backend resolves the mapping for the authenticated actor.
 */
export default function ExecutorTerminalIdentitiesPanel({ apiClient, executor, setError }: Props) {
  const [identities, setIdentities] = useState<ExecutorTerminalIdentity[]>([]);
  const [email, setEmail] = useState('');
  const advertisedAccounts = useMemo(
    () => executor.capabilities?.terminal_identity_accounts ?? [],
    [executor.capabilities]
  );
  const [managedAccounts, setManagedAccounts] = useState<TerminalIdentityAccount[] | null>(null);
  const accounts = managedAccounts ?? advertisedAccounts;
  const advertisedAccountsJson = JSON.stringify(advertisedAccounts);
  const [localUsername, setLocalUsername] = useState('');
  const [allowlistDraft, setAllowlistDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAllowlist, setSavingAllowlist] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const inventoryReady = executor.capabilities?.terminal_identities_v1 === true && accounts.length > 0;
  const allowlistManagementAvailable = executor.capabilities?.terminal_identity_allowlist_v1 === true;

  useEffect(() => {
    const currentAdvertisedAccounts = JSON.parse(advertisedAccountsJson) as TerminalIdentityAccount[];
    setManagedAccounts(null);
    setAllowlistDraft(currentAdvertisedAccounts
      .map((account) => account.username)
      .join('\n'));
  }, [advertisedAccountsJson, executor.id]);

  useEffect(() => {
    setLocalUsername((current) => accounts.some((account) => account.username === current)
      ? current
      : accounts[0]?.username ?? '');
  }, [accounts]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await apiClient.get<ExecutorTerminalIdentity[]>(
        `/api/executors/nodes/${encodeURIComponent(executor.id)}/terminal/identities`
      );
      setIdentities(rows ?? []);
      setLocalError(null);
    } catch (error) {
      reportErrorLocal('Failed to load terminal identities', error, setError, setLocalError);
    } finally {
      setLoading(false);
    }
  }, [apiClient, executor.id, setError]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail || !localUsername || saving) return;
    setSaving(true);
    setMessage(null);
    try {
      const saved = await apiClient.post<ExecutorTerminalIdentity>(
        `/api/executors/nodes/${encodeURIComponent(executor.id)}/terminal/identities`,
        { user_email: normalizedEmail, local_username: localUsername }
      );
      setIdentities((current) => [saved, ...current.filter((identity) => identity.user_id !== saved.user_id)]);
      setEmail('');
      setLocalUsername(accounts[0]?.username ?? '');
      setMessage(`${userLabel(saved)} is mapped to ${saved.local_username}.`);
      setLocalError(null);
    } catch (error) {
      reportErrorLocal('Failed to save terminal identity', error, setError, setLocalError);
    } finally {
      setSaving(false);
    }
  };

  const saveAllowlist = async () => {
    if (savingAllowlist || !allowlistManagementAvailable) return;
    const localUsernames = [...new Set(allowlistDraft
      .split(/\r?\n/)
      .map((value) => value.trim())
      .filter(Boolean))];
    const nextAccounts = new Set(localUsernames);
    const currentAccounts = new Set(accounts.map((account) => account.username));
    const mappingsLosingAccess = identities.filter((identity) =>
      currentAccounts.has(identity.local_username) && !nextAccounts.has(identity.local_username)
    );
    const confirmation = [
      localUsernames.length === 0
        ? 'Remove every allowed local account? Existing mappings will remain but cannot start new terminal sessions until an account is allowed again.'
        : null,
      mappingsLosingAccess.length > 0
        ? `${mappingsLosingAccess.length} active mapping${mappingsLosingAccess.length === 1 ? '' : 's'} will no longer be able to start a terminal. Continue?`
        : null
    ].filter(Boolean).join('\n\n');
    if (confirmation && !window.confirm(confirmation)) {
      return;
    }
    setSavingAllowlist(true);
    setMessage(null);
    try {
      const response = await apiClient.put<TerminalIdentityAllowlistResponse>(
        `/api/executors/nodes/${encodeURIComponent(executor.id)}/terminal/identities/allowlist`,
        { local_usernames: localUsernames, confirm_empty: localUsernames.length === 0 }
      );
      const configuredAccounts = response.accounts ?? [];
      setManagedAccounts(configuredAccounts);
      setAllowlistDraft(configuredAccounts.map((account) => account.username).join('\n'));
      setMessage(configuredAccounts.length > 0
        ? `Allowed local accounts updated (${configuredAccounts.length}).`
        : 'All allowed local accounts were removed.');
      setLocalError(null);
    } catch (error) {
      reportErrorLocal('Failed to update allowed local accounts', error, setError, setLocalError);
    } finally {
      setSavingAllowlist(false);
    }
  };

  const edit = (identity: ExecutorTerminalIdentity) => {
    setEmail(identity.user_email ?? '');
    setLocalUsername(identity.local_username);
    setMessage(identity.user_email
      ? `Editing ${userLabel(identity)}. Saving replaces this executor mapping.`
      : 'This identity has no email available and cannot be edited from the email-only form. Revoke it instead.');
  };

  const revoke = async (identity: ExecutorTerminalIdentity) => {
    if (revokingId || !window.confirm(`Revoke terminal access for ${userLabel(identity)}?`)) return;
    setRevokingId(identity.id);
    setMessage(null);
    try {
      await apiClient.del(
        `/api/executors/nodes/${encodeURIComponent(executor.id)}/terminal/identities/${encodeURIComponent(identity.id)}`
      );
      setIdentities((current) => current.filter((row) => row.id !== identity.id));
      setMessage(`Terminal access revoked for ${userLabel(identity)}.`);
      setLocalError(null);
    } catch (error) {
      reportErrorLocal('Failed to revoke terminal identity', error, setError, setLocalError);
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <section className="card full stack terminal-identities" aria-labelledby="terminal-identities-title">
      <div className="card-header">
        <div>
          <h3 id="terminal-identities-title">Terminal identities</h3>
          <p className="status">Map a Mission Control user to one explicitly allowed non-root account on this executor.</p>
        </div>
        <button type="button" className="secondary" onClick={() => void load()} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {localError && <ErrorBanner message={localError} onDismiss={() => setLocalError(null)} />}
      {message && <p className="status status-text--ok">{message}</p>}

      <div className="terminal-identities__allowlist">
        <div>
          <h4>Allowed local accounts</h4>
          <p className="status">
            These are the non-root interactive accounts this executor may use for mapped browser terminals.
            Changes are applied by the root-run executor service and are recorded in the executor audit log.
          </p>
        </div>
        {allowlistManagementAvailable ? (
          <>
            <label>
              Local account names
              <textarea
                value={allowlistDraft}
                onChange={(event) => setAllowlistDraft(event.target.value)}
                placeholder={'alice\ndeployer'}
                rows={Math.max(3, Math.min(8, accounts.length + 1))}
                spellCheck={false}
                disabled={savingAllowlist}
              />
              <span className="hint">
                One existing non-root login account per line. System accounts, root, and accounts without an
                interactive shell are rejected by the executor.
              </span>
            </label>
            <button type="button" onClick={() => void saveAllowlist()} disabled={savingAllowlist}>
              {savingAllowlist ? 'Applying...' : 'Apply allowed accounts'}
            </button>
          </>
        ) : (
          <p className="warning-text">
            This executor needs a current runner and reconnect before allowed local accounts can be managed here.
            Existing mappings can still be revoked below.
          </p>
        )}
      </div>

      {inventoryReady ? (
        <div className="terminal-identities__form">
          <label>
            Product user email
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="person@example.net"
              autoComplete="email"
            />
            <span className="hint">An exact email match is required; the server resolves the product user ID and does not search or expose a user directory.</span>
          </label>
          <label>
            Local account
            <select value={localUsername} onChange={(event) => setLocalUsername(event.target.value)}>
              {accounts.map((account) => (
                <option key={account.username} value={account.username}>{accountLabel(account)}</option>
              ))}
            </select>
            <span className="hint">
              Only host-allowlisted accounts are shown; each local account can map to one product user.
            </span>
          </label>
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving || !email.trim() || !localUsername}
          >
            {saving ? 'Saving...' : 'Save mapping'}
          </button>
        </div>
      ) : (
        <p className="warning-text">
          {allowlistManagementAvailable
            ? 'No usable local accounts are currently allowed. Add one above before creating or editing mappings.'
            : 'This executor has not advertised a usable terminal-identity allowlist.'}
        </p>
      )}

      <div className="table-scroll">
        <table className="responsive-table terminal-identities__table">
          <thead>
            <tr><th>Product user</th><th>Local account</th><th>Updated</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {identities.map((identity) => (
              <tr key={identity.id}>
                <td data-label="Product user">
                  <strong>{userLabel(identity)}</strong>
                  {identity.user_name?.trim() && identity.user_email?.trim() && <span className="hint">{identity.user_email}</span>}
                </td>
                <td data-label="Local account"><code>{identity.local_username}</code></td>
                <td data-label="Updated">{new Date(identity.updated_at).toLocaleString()}</td>
                <td data-label="Actions" className="responsive-table__actions">
                  <div className="row row--compact">
                    <button
                      type="button"
                      className="secondary compact"
                      onClick={() => edit(identity)}
                      disabled={
                        !inventoryReady
                        || !identity.user_email
                        || !accounts.some((account) => account.username === identity.local_username)
                      }
                      title={
                        !inventoryReady
                          ? 'The executor has not advertised available local accounts.'
                          : !identity.user_email
                            ? 'This mapping has no email available for the email-only form.'
                            : !accounts.some((account) => account.username === identity.local_username)
                              ? 'This mapping references a local account that is no longer allowlisted. Revoke it instead.'
                              : undefined
                      }
                    >
                      Edit
                    </button>
                    <button type="button" className="danger compact" onClick={() => void revoke(identity)} disabled={revokingId === identity.id}>
                      {revokingId === identity.id ? 'Revoking...' : 'Revoke'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && identities.length === 0 && (
              <tr className="responsive-table__empty"><td colSpan={4}>No user-to-local-account mappings are active for this executor.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="hint">
        This mapping only controls browser terminals started through Mission Control. It does not create a Unix
        account or grant sudo. Each selected local account has exactly the SSH and sudoers privileges configured on the host.
      </p>
    </section>
  );
}
